import { Component } from '@angular/core'
import { DialogRef } from '@angular/cdk/dialog'
import { selectImageLayers, selectSegmentationLayers } from 'src/app/state/viewer/viewer.selectors'
import { Store } from '@ngrx/store'
import { StaticLayerConfig } from 'src/app/state/viewer/viewer.state'
import { selectLoadedSegObjectPropertyMaps } from 'src/app/state/case/case.selectors'
import { PipelineType } from 'src/app/shared/model/jobs'
import { HttpErrorResponse } from '@angular/common/http'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { concatMap, from, take } from 'rxjs'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { JobApiService } from 'src/app/shared/services/job-api.service'

interface TreeNode {
    id: string
    show: boolean
    selected: boolean
    isTopLevel?: boolean
    nodes: TreeNode[]
}

@Component({
    selector: 'app-add-properties-dialog',
    templateUrl: './add-properties-dialog.component.html',
    styleUrls: ['./add-properties-dialog.component.scss'],
})
export class AddPropertiesDialogComponent {
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)
    readonly markerLayers$ = this.store.select(selectImageLayers)
    radius: number = 1
    summary: string = 'mean'
    private segmentationLayers: StaticLayerConfig[] = []

    nodes: TreeNode[] = [
        {
            id: 'Segmentations',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            id: 'Markers',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
    ]

    constructor(
        readonly store: Store,
        private readonly dialogRef: DialogRef<string>,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.store.select(selectSegmentationLayers).subscribe(segmentationLayers => {
            this.segmentationLayers = segmentationLayers
            const segmentationNode = this.nodes.find(node => node.id === 'Segmentations')
            if (segmentationNode) {
                segmentationNode.nodes = segmentationLayers.map(layer => ({
                    id: layer.name!,
                    show: false,
                    selected: false,
                    nodes: [],
                }))
            }
        })

        this.markerLayers$.subscribe(markerLayers => {
            const markersNode = this.nodes.find(node => node.id === 'Markers')
            if (markersNode) {
                markersNode.nodes = markerLayers.map(layer => ({
                    id: layer.name!,
                    show: false,
                    selected: false,
                    nodes: [],
                }))
            }
        })
    }

    cancel(): void {
        this.dialogRef.close(undefined)
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected
        if (node.isTopLevel && node.nodes.length > 0) {
            node.nodes.forEach(child => {
                child.selected = node.selected
            })
        }
    }

    confirm(): void {
        this.loadedSegObjectPropertyMaps$.pipe(take(1)).subscribe(loadedMaps => {
            if (loadedMaps.length === 0) {
                this.toaster.error('Select an intensity map first', 'Error')
                this.dialogRef.close()
                return
            }

            const selectedSources: { [key: string]: string[] } = {}
            let hasRadiusAndSummary = false

            for (let i = 0; i < 2; i++) {
                if (this.markerIsSelected(this.nodes[i])) {
                    const selectedNodes = this.nodes[i].nodes.filter(n => n.selected).map(n => n.id)
                    selectedSources[this.nodes[i].id.toLocaleLowerCase()] = selectedNodes!

                    if (i === 1) {
                        hasRadiusAndSummary = true
                    }
                }
            }

            if (Object.keys(selectedSources).length === 0) {
                this.dialogRef.close()
                return
            }

            const jobs = loadedMaps.map(map => {
                const targetLayer = this.segmentationLayers.find(
                    layer => layer.source === map.segSource
                )
                const jobParams: any = {
                    type: PipelineType.AddMappingProperties,
                    target: targetLayer?.name,
                    source: selectedSources,
                    segObjectPropertyMap: map,
                    mask: undefined,
                    batch: false,
                }
                if (hasRadiusAndSummary) {
                    jobParams.radius = this.radius
                    jobParams.summary = this.summary
                }
                return jobParams
            })

            const missingTarget = jobs.find(j => !j.target)
            if (missingTarget) {
                this.toaster.error(
                    'Could not resolve a segmentation layer for one or more intensity maps.',
                    'Error starting job'
                )
                this.dialogRef.close()
                return
            }

            // One ADP job per intensity map (not a joint multi-map job).
            from(jobs)
                .pipe(concatMap(params => this.jobApi.startIntensityJob(params)))
                .subscribe({
                    next: () => undefined,
                    complete: () => {
                        this.dialogRef.close()
                        closeJobSelectionDialog()
                    },
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Error starting job')
                        this.dialogRef.close()
                    },
                })
        })
    }

    markerIsSelected(node: TreeNode): boolean {
        return node.nodes.find(n => n.selected) !== undefined
    }
}
